import React, {useCallback} from 'react';
import {useHistory} from 'react-router-dom';
import {useSnackbar} from 'notistack';
import {ChatbotPopup} from '@setupad/ask-workbench-react';

import usePersistentAuthStore from '../../store/persistentAuthStore';
import useStore from '../../store/store';

// Workbench origin for the ask-workbench chatbot (no trailing /api; routes carry the path).
const CHATBOT_HOST =
  process.env.NODE_ENV === 'development'
    ? (process.env.REACT_APP_WORKBENCH_URL ?? '').replace(/\/api\/?$/, '')
    : process.env.REACT_APP_CURRENT_BRANCH === 'master'
      ? 'https://wb.setupad.com'
      : 'https://wb-api-dev.setupad.com';

// Renders the Ask Workbench chatbot and bridges its client-side actions into the
// module-federated Prebid Panel (which runs in this same window). Subscribes to auth
// and the selected property here so the host App no longer re-renders on those changes.
function ClientChatbot() {
  const {enqueueSnackbar} = useSnackbar();
  const history = useHistory();
  const auth = usePersistentAuthStore(state => state.auth);
  const selection = useStore(state => state.selection);

  // Context sent with every chatbot request: client auth + the currently selected property.
  const getContext = useCallback(
    () => ({
      token: auth?.token,
      reportingToken: auth?.reportingToken,
      accountId: auth?.accountId,
      selection: selection?.value,
    }),
    [auth, selection]
  );

  const handleAction = useCallback(
    action => {
      if (!action || action.type !== 'stageTagTimeout') return;
      const bridge = window.__setupadPrebidPanel;
      // The bridge only exists while the Prebid Panel is mounted.
      if (!bridge || typeof bridge.stageTagTimeout !== 'function') {
        enqueueSnackbar('Open the Prebid Panel first, then ask again to stage the change.', {
          variant: 'info',
        });
        history.push('/prebid-panel');
        return;
      }
      const result = bridge.stageTagTimeout(action);
      enqueueSnackbar(result?.message || 'Change staged in the Prebid Panel.', {
        variant: result?.ok ? 'success' : 'warning',
      });
    },
    [enqueueSnackbar, history]
  );

  return (
    <ChatbotPopup
      config={{
        host: CHATBOT_HOST,
        routes: {
          aiQuery: 'api/client/ai/query',
          aiQueryStream: 'api/client/ai/query/stream',
        },
      }}
      title="Ask Revi"
      getContext={getContext}
      onAction={handleAction}
      greeting="Hi, I'm Revi! I can help with your Setupad setup and onboarding, explain your reporting, and guide you through Prebid Panel changes like tag timeouts. What would you like to do?"
      suggestions={[
        "What's my onboarding status?",
        'What are my next setup steps?',
        "How do I change a tag's timeout?",
      ]}
    />
  );
}

export default ClientChatbot;
